import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import Map, { MapRef, Marker } from 'react-map-gl';

import useLanguage from 'hooks/useLanguage';
import { Mixpanel } from 'mixpanel';
import useFocusedModule from 'hooks/useFocusedModule';
import Select from 'components/Select';
import ProspectMapModal from 'components/ProspectMapModal';
import useProspects from 'api/hooks/useProspects';
import { ProspectOneProps, ProspectProps } from 'utils/types';
import useDatasetById from 'api/hooks/getDatasetById';

import bluePin from '/pinBlue.svg';
import greyPin from '/greyPin.svg';

import i18n from 'localization/i18n';
import useUser from 'api/hooks/useUser';

const initialViewState = {
  latitude: 46.8182,
  longitude: 8.2275,
  zoom: 7,
};

const ProspectMap = () => {
  const mapRef = useRef<MapRef | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modal, setModal] = useState<boolean>(false);
  const { data: prospects } = useProspects();
  const [selectedDataSet, setSelectedDataSet] = useState<ProspectProps | null>(
    null,
  );
  const { data: datasetById, refetch } = useDatasetById({
    id: selectedDataSet?.id,
  });
  const [selectedProspect, setSelectedProspect] =
    useState<ProspectOneProps | null>(null);
  const { blueLang } = useLanguage();
  const { data } = useUser();

  useFocusedModule('Prospect Map Module Duration');

  useEffect(() => {
    if (blueLang.length > 0) {
      i18n.changeLanguage(blueLang);
    }
  }, [blueLang]);

  const selectOptions = useMemo(() => {
    if (prospects) {
      return prospects?.map((prospect) => ({ value: String(prospect.id),
        label: prospect.title }));
    }

    return [];
  }, [prospects]);

  const onChangeSelect = (id: string) => {
    const filteredProspect = prospects?.find(
      (prospect) => prospect.id === Number(id),
    );

    if (filteredProspect) {
      setSelectedDataSet(filteredProspect);
    }
  };

  useEffect(() => {
    if (datasetById) {
      if (
        datasetById?.prospects?.[0]?.latitude &&
        datasetById?.prospects?.[0]?.longtitude
      ) {
        mapRef.current?.flyTo({
          center: [
            Number(datasetById?.prospects?.[0]?.longtitude),
            Number(datasetById?.prospects?.[0]?.latitude),
          ],
          duration: 2000,
        });
      }
    }
  }, [datasetById]);

  useEffect(() => {
    if (data) {
      Mixpanel.track('Prospect Map Opened', {
        role: data?.role?.name,
        organization: data?.organization,
      });
    }
  }, [data]);

  return (
    <>
      <div className="px-[100px] py-[32px] md:px-[16px] md:pb-4 flex flex-col h-full">
        <div className="mb-8">
          <span
            onClick={() => navigate('/')}
            className="text-secondaryText cursor-pointer"
          >
            {t('home')} /
          </span>
          <span className="font-medium"> {t('prospectMap')}</span>
        </div>
        <div className="w-[358px] md:w-[345px]">
          <Select
            title={t('chooseProspect')}
            options={selectOptions}
            onChange={(el) => onChangeSelect(el?.value)}
            placeholder={t('chooseProspect')}
            value={[{
              value: selectedDataSet?.title ?? '',
              label: selectedDataSet?.title ?? '',
            }]}
          />
        </div>
      </div>
      <div>
        {modal && (
          <ProspectMapModal
            selectedProspect={selectedProspect}
            setModal={setModal}
            refetch={refetch}
            selectedDataSetId={selectedDataSet?.id}
          />
        )}
        <Map
          mapboxAccessToken={import.meta.env.VITE_APP_MAP_TOKEN}
          ref={(ref) => (mapRef.current = ref)}
          initialViewState={initialViewState}
          style={{ width: '100vw',
            height: 500 }}
          mapStyle="mapbox://styles/mapbox/streets-v9"
        >
          {datasetById && (
            <>
              {datasetById?.prospects?.map((prospect) => (
                <Marker
                  key={prospect.id}
                  longitude={Number(prospect.longtitude)}
                  latitude={Number(prospect.latitude)}
                  onClick={() => {
                    setSelectedProspect(prospect);
                    setModal(true);
                    Mixpanel.track('Prospect Map Pins', {
                      role: data?.role?.name,
                      organization: data?.organization,
                    });
                  }}
                >
                  {prospect?.status.toLocaleLowerCase() === 'not contacted' ? (
                    <img src={greyPin} />
                  ) : (
                    <img src={bluePin} />
                  )}
                </Marker>
              ))}
            </>
          )}
        </Map>
      </div>
    </>
  );
};

export default ProspectMap;
